import { presignUpload, uploadFileToPresignedUrl, finalizeManualTranscript, retryBillingStart } from '../api.js';
import { getCurrentUser } from '../auth.js';
import { getUploadCopy } from '../i18n.js';

const html = String.raw;
const appConfig = window.__ANFORDERUNGSAI_CONFIG__ ?? {};

const getTodayDateString = () => {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
};

const isPdfFile = (file) => {
  if (!file) return false;
  if (file.type === 'application/pdf') return true;
  return /\.pdf$/i.test(file.name ?? '');
};

const isDocxFile = (file) => {
  if (!file) return false;
  if (file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') return true;
  return /\.docx$/i.test(file.name ?? '');
};

const isDocFile = (file) => {
  if (!file) return false;
  if (file.type === 'application/msword') return true;
  return /\.doc$/i.test(file.name ?? '');
};

const MEDIA_FILE_EXTENSIONS = new Set(['mp3', 'mp4', 'wav', 'flac', 'm4a', 'webm', 'ogg', 'mov', 'qta']);
const TRANSCRIPT_FILE_EXTENSIONS = new Set(['txt', 'md', 'markdown', 'json', 'csv', 'srt', 'vtt', 'rtf', 'pdf', 'docx', 'doc']);

const getFileExtension = (file) => {
  const name = String(file?.name ?? '');
  const index = name.lastIndexOf('.');
  if (index === -1) return '';
  return name.slice(index + 1).toLowerCase();
};

const isMediaFile = (file) => {
  if (!file) return false;
  const type = String(file.type ?? '').toLowerCase();
  if (type.startsWith('audio/') || type.startsWith('video/')) return true;
  return MEDIA_FILE_EXTENSIONS.has(getFileExtension(file));
};

const isTranscriptFile = (file) => {
  if (!file) return false;
  const ext = getFileExtension(file);
  if (TRANSCRIPT_FILE_EXTENSIONS.has(ext)) return true;
  const type = String(file.type ?? '').toLowerCase();
  return (
    type.startsWith('text/') ||
    type === 'application/json' ||
    type === 'text/csv' ||
    type === 'text/vtt' ||
    type === 'text/x-subrip' ||
    type === 'application/pdf' ||
    type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
    type === 'application/msword'
  );
};

const resolveUploadRoute = (file) => {
  if (isMediaFile(file)) return 'media';
  if (isTranscriptFile(file)) return 'transcript';
  return 'unknown';
};

const extractPdfText = async (file) => {
  const buffer = await file.arrayBuffer();
  const pdfjs = await import('pdfjs-dist/legacy/build/pdf.mjs');
  if (!pdfjs.GlobalWorkerOptions?.workerSrc) {
    pdfjs.GlobalWorkerOptions.workerSrc = './vendor/pdfjs/pdf.worker.min.mjs';
  }
  const loadingTask = pdfjs.getDocument({ data: buffer });
  const pdf = await loadingTask.promise;
  const pages = [];
  for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
    const page = await pdf.getPage(pageNumber);
    const content = await page.getTextContent();
    const pageText = content.items.map((item) => item.str).join(' ').trim();
    if (pageText) {
      pages.push(pageText);
    }
  }
  return pages.join('\n\n');
};

const extractDocxText = async (file) => {
  const buffer = await file.arrayBuffer();
  const module = await import('mammoth/mammoth.browser.js');
  const mammoth = module.default ?? module;
  const result = await mammoth.extractRawText({ arrayBuffer: buffer });
  return result?.value ?? '';
};

const renderSuccess = (copy, result) => html`
  <div class="card stack">
    <h2>${copy.successTitle}</h2>
    <p>${copy.conversationLabel}: <code>${result.conversationId}</code></p>
    <div class="row">
      <button type="button" data-navigate="#/conversation/${result.conversationId}">${copy.viewJobs}</button>
      <button type="button" data-copy="${result.conversationId}">${copy.copyId}</button>
    </div>
    <p class="muted">${copy.successNote}</p>
  </div>
`;

const bindResultActions = (root, copy) => {
  if (!root) return;
  root.querySelectorAll('[data-navigate]').forEach((button) => {
    button.addEventListener('click', (e) => {
      window.location.hash = e.currentTarget.dataset.navigate;
    });
  });

  root.querySelectorAll('[data-copy]').forEach((button) => {
    button.addEventListener('click', async (e) => {
      await navigator.clipboard.writeText(e.currentTarget.dataset.copy);
      e.currentTarget.textContent = copy.copySuccess;
      setTimeout(() => {
        e.currentTarget.textContent = copy.copyId;
      }, 2000);
    });
  });
};

const buildSuccessCopy = (baseCopy, overrides = {}) => ({
  ...baseCopy,
  successTitle: overrides.successTitle ?? baseCopy.successTitle,
  successNote: overrides.successNote ?? baseCopy.successNote,
  conversationLabel: overrides.conversationLabel ?? baseCopy.conversationLabel,
  viewJobs: overrides.viewJobs ?? baseCopy.viewJobs,
  copyId: overrides.copyId ?? baseCopy.copyId,
  copySuccess: overrides.copySuccess ?? baseCopy.copySuccess,
});

const handleBillingRetryToken = (error) => {
  const retryToken = error?.payload?.retryToken;
  if (typeof retryToken === 'string' && retryToken.trim()) {
    window.sessionStorage.setItem('billingUploadRetryToken', retryToken.trim());
  }
};

const mapUploadErrorMessage = (error, fallbackMessage) => {
  const code = error?.payload?.code;
  if (code === 'BILLING_REQUIRED' || code === 'PLAN_CHANGE_PENDING' || code === 'PAYMENT_FAILED') {
    return `${error?.message ?? fallbackMessage} Öffne Profil > Abrechnungen.`;
  }
  if (code === 'PACKAGE_M_LIMIT_REACHED') {
    return `${error?.message ?? fallbackMessage} Wähle Pay per Use, Top-up oder Unlimited im Profil.`;
  }
  return error?.message ?? fallbackMessage;
};

const createUploadIntentWithRetry = async (payload) => {
  const retryToken = window.sessionStorage.getItem('billingUploadRetryToken');
  if (retryToken) {
    try {
      const retried = await retryBillingStart({ retryToken });
      if (retried?.retryConsumed) {
        window.sessionStorage.removeItem('billingUploadRetryToken');
      }
      return retried;
    } catch (error) {
      if (error?.payload?.retryToken) {
        handleBillingRetryToken(error);
        throw error;
      }
      window.sessionStorage.removeItem('billingUploadRetryToken');
      if (error?.status !== 400 && error?.status !== 409) {
        throw error;
      }
    }
  }
  const presign = await presignUpload(payload);
  window.sessionStorage.removeItem('billingUploadRetryToken');
  return presign;
};

export const renderUploadView = (container, options = {}) => {
  const copy = getUploadCopy();
  const user = getCurrentUser();
  const variant = options.variant ?? 'default';
  const isNewRequirements = variant === 'new-requirements';
  const transcriptCopy = copy.transcript ?? {};
  const transcriptSuccessCopy = buildSuccessCopy(copy, transcriptCopy);
  const uploadTitle = options.uploadTitle ?? copy.title;
  const uploadDescription = options.description ?? copy.description;
  const transcriptTitle = options.transcriptTitle ?? transcriptCopy.title ?? copy.title;
  const transcriptDescription = options.transcriptDescription ?? transcriptCopy.description;
  const uploadFormatsHint = options.uploadFormatsHint ?? copy.formatsHint ?? '';
  const transcriptFormatsHint = options.transcriptFormatsHint ?? transcriptCopy.formatsHint ?? '';
  const showDescription = options.showDescription ?? !isNewRequirements;
  const showEngineDescription = options.showEngineDescription ?? !isNewRequirements;
  const showTranscriptDescription = options.showTranscriptDescription ?? !isNewRequirements;
  const showTranscriptFormatsHint = options.showTranscriptFormatsHint ?? !isNewRequirements;
  const showPlanNote = options.showPlanNote ?? !isNewRequirements;
  const showUploadFormatsTooltip = options.showUploadFormatsTooltip ?? isNewRequirements;
  const showTranscriptFormatsTooltip = options.showTranscriptFormatsTooltip ?? isNewRequirements;
  const transcriptFormats = showTranscriptFormatsHint ? transcriptFormatsHint : '';
  const stripOptional = (label) => {
    if (!label) return label;
    return label.replace(/\s*\((optional|optionnel|opcional)\)\s*/gi, '').trim();
  };
  const customerLabel = isNewRequirements ? stripOptional(copy.customerLabel) : copy.customerLabel;
  const projectLabel = isNewRequirements ? stripOptional(copy.projectLabel) : copy.projectLabel;
  const meetingLabel = isNewRequirements ? stripOptional(copy.meetingLabel) : copy.meetingLabel;
  const transcriptCustomerLabel = isNewRequirements
    ? stripOptional(transcriptCopy.customerLabel ?? copy.customerLabel)
    : (transcriptCopy.customerLabel ?? copy.customerLabel);
  const transcriptProjectLabel = isNewRequirements
    ? stripOptional(transcriptCopy.projectLabel ?? copy.projectLabel)
    : (transcriptCopy.projectLabel ?? copy.projectLabel);
  const transcriptMeetingLabel = isNewRequirements
    ? stripOptional(transcriptCopy.meetingLabel ?? copy.meetingLabel)
    : (transcriptCopy.meetingLabel ?? copy.meetingLabel);
  const uploadFileLabel = html`
    <span class="input-label">
      ${copy.fileLabel}
      ${showUploadFormatsTooltip && uploadFormatsHint
        ? `<span class="info-tooltip" tabindex="0" role="button" aria-label="${uploadFormatsHint}" data-tooltip="${uploadFormatsHint}">i</span>`
        : ''}
    </span>
  `;
  const transcriptFileLabel = html`
    <span class="input-label">
      ${transcriptCopy.fileLabel ?? copy.fileLabel}
      ${showTranscriptFormatsTooltip && transcriptFormatsHint
        ? `<span class="info-tooltip" tabindex="0" role="button" aria-label="${transcriptFormatsHint}" data-tooltip="${transcriptFormatsHint}">i</span>`
        : ''}
    </span>
  `;
  const ENGINE_CATALOG = {
    mistral: {
      code: 'mistral',
      label: copy.engines?.mistral ?? 'Mistral (Voxtral)',
    },
    'aws-transcribe': {
      code: 'aws-transcribe',
      label: copy.engines?.aws ?? 'AWS Transcribe',
    },
  };
  const configuredEngines = Array.isArray(appConfig.transcriptionEngines)
    ? appConfig.transcriptionEngines.map((value) => String(value).toLowerCase())
    : ['mistral', 'aws-transcribe'];
  let ENGINE_OPTIONS = configuredEngines
    .map((code) => ENGINE_CATALOG[code])
    .filter(Boolean);
  if (!ENGINE_OPTIONS.length) {
    ENGINE_OPTIONS = [ENGINE_CATALOG.mistral];
  }
  const configuredDefaultEngine = typeof appConfig.transcriptionEngineDefault === 'string'
    ? String(appConfig.transcriptionEngineDefault).toLowerCase()
    : ENGINE_OPTIONS[0]?.code;
  const preferredDefaultEngine = isNewRequirements ? 'aws-transcribe' : configuredDefaultEngine;
  const DEFAULT_ENGINE = (ENGINE_OPTIONS.find((option) => option.code === preferredDefaultEngine)
    ?? ENGINE_OPTIONS[0]
    ?? ENGINE_CATALOG.mistral).code;
  const DOMAIN_OPTIONS = Array.isArray(copy.domainOptions) && copy.domainOptions.length
    ? copy.domainOptions
    : [
        { value: 'enterprise-software', label: 'Enterprise-Software' },
        { value: 'bau', label: 'Bau' },
        { value: 'maschinenbau', label: 'Maschinenbau' },
        { value: 'produktentwicklung', label: 'Produktentwicklung' },
        { value: 'sonstiges', label: 'Sonstiges' },
      ];
  const DEFAULT_DOMAIN = copy.domainDefault ?? DOMAIN_OPTIONS[0]?.value ?? 'enterprise-software';
  const engineButtonsMarkup = ENGINE_OPTIONS.map((option) => html`<button
      type="button"
      class="language-pill${option.code === DEFAULT_ENGINE ? ' active' : ''}"
      data-engine="${option.code}"
      aria-pressed="${option.code === DEFAULT_ENGINE ? 'true' : 'false'}"
    >${option.label}</button>`).join('');
  const engineSelectorMarkup = html`
    <div class="stack tight">
      <span class="muted small">${copy.engineLabel ?? 'Engine'}</span>
      <div class="row" id="upload-engine-selector">
        ${engineButtonsMarkup}
      </div>
      ${showEngineDescription && copy.engineDescription ? `<p class="muted tiny">${copy.engineDescription}</p>` : ''}
    </div>
  `;
  const uploadCardClass = isNewRequirements ? 'card stack upload-card' : 'card stack';
  const transcriptCardClass = isNewRequirements ? 'card stack upload-card' : 'card stack';
  const transcriptStatusText = (key, fallback) => transcriptCopy.status?.[key] ?? copy[key] ?? fallback ?? '';
  const combinedUploadAccept = 'audio/*,video/*,.qta,.txt,.md,.markdown,.json,.csv,.srt,.vtt,.rtf,.pdf,.docx,.doc,text/plain,application/json,text/csv,text/vtt,text/x-subrip,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword';
  const transcriptUploadAccept = '.txt,.md,.markdown,.json,.csv,.srt,.vtt,.rtf,.pdf,.docx,text/plain,application/json,text/csv,text/vtt,text/x-subrip,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document';

  if (isNewRequirements) {
    container.innerHTML = html`
      <div class="stack" style="gap:24px;">
        <div class="${uploadCardClass}">
          <h2>${uploadTitle}</h2>
          ${showDescription && uploadDescription ? `<p class="muted">${uploadDescription}</p>` : ''}
          <form class="stack" id="unified-upload-form">
            <input type="file" name="file" accept="${combinedUploadAccept}" hidden />
            <div class="upload-drop-zone" id="unified-upload-drop-zone" role="button" tabindex="0" aria-label="${copy.fileLabel}">
              <strong>${copy.fileLabel}</strong>
              ${uploadFormatsHint ? `<p class="muted tiny" style="margin:0;">${uploadFormatsHint}</p>` : ''}
              ${transcriptFormatsHint ? `<p class="muted tiny" style="margin:0;">${transcriptFormatsHint}</p>` : ''}
              <p class="muted tiny upload-drop-zone__file" id="unified-upload-file-name">Keine Datei ausgewählt</p>
            </div>
            <div class="row">
              <label style="flex:1">
                ${customerLabel}
                <input type="text" name="customerId" placeholder="${copy.customerPlaceholder}" />
              </label>
              <label style="flex:1">
                ${projectLabel}
                <input type="text" name="projectId" placeholder="${copy.projectPlaceholder}" />
              </label>
              <label style="flex:1">
                ${meetingLabel ?? 'Meeting'}
                <input type="date" name="meetingDate" />
              </label>
              <label style="flex:1">
                ${copy.domainLabel ?? 'Themenbereich'}
                <select name="domain">
                  ${DOMAIN_OPTIONS.map(
                    (option) => `<option value="${option.value}"${option.value === DEFAULT_DOMAIN ? ' selected' : ''}>${option.label}</option>`,
                  ).join('')}
                </select>
              </label>
            </div>
            ${engineSelectorMarkup}
            <div class="form-actions">
              <button type="submit">${copy.button}</button>
              <p class="muted" id="unified-upload-status"></p>
            </div>
          </form>
          <div id="unified-upload-result"></div>
        </div>
      </div>
    `;

    const form = container.querySelector('#unified-upload-form');
    const fileInput = form.querySelector('input[name="file"]');
    const dropZone = container.querySelector('#unified-upload-drop-zone');
    const fileNameEl = container.querySelector('#unified-upload-file-name');
    const statusEl = container.querySelector('#unified-upload-status');
    const resultEl = container.querySelector('#unified-upload-result');
    const submitButton = form.querySelector('button[type="submit"]');
    const meetingInput = form.querySelector('input[name="meetingDate"]');
    const engineButtons = Array.from(container.querySelectorAll('#upload-engine-selector [data-engine]'));

    let selectedFile = null;
    let selectedEngine = DEFAULT_ENGINE;

    const updateEngineSelection = (engine) => {
      if (!engine) return;
      selectedEngine = engine;
      engineButtons.forEach((button) => {
        const isActive = button.dataset.engine === engine;
        button.classList.toggle('active', isActive);
        button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });
    };
    engineButtons.forEach((button) => {
      button.addEventListener('click', () => updateEngineSelection(button.dataset.engine));
    });
    updateEngineSelection(DEFAULT_ENGINE);

    if (meetingInput && !meetingInput.value) {
      meetingInput.value = getTodayDateString();
    }

    const updateSelectedFile = (file) => {
      selectedFile = file ?? null;
      if (!fileNameEl) return;
      fileNameEl.textContent = selectedFile ? selectedFile.name : 'Keine Datei ausgewählt';
    };

    dropZone?.addEventListener('click', () => fileInput?.click());
    dropZone?.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        fileInput?.click();
      }
    });
    dropZone?.addEventListener('dragover', (event) => {
      event.preventDefault();
      dropZone.classList.add('drag-active');
    });
    dropZone?.addEventListener('dragleave', () => {
      dropZone.classList.remove('drag-active');
    });
    dropZone?.addEventListener('drop', (event) => {
      event.preventDefault();
      dropZone.classList.remove('drag-active');
      const file = event.dataTransfer?.files?.[0];
      if (file) {
        updateSelectedFile(file);
      }
    });
    fileInput?.addEventListener('change', () => {
      updateSelectedFile(fileInput.files?.[0] ?? null);
    });

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      const file = selectedFile ?? fileInput?.files?.[0];
      if (!file) {
        statusEl.textContent = copy.selectFileWarning;
        return;
      }

      const route = resolveUploadRoute(file);
      if (route === 'unknown') {
        statusEl.textContent = transcriptCopy.status?.unsupportedType ?? 'Dateityp wird nicht unterstuetzt.';
        return;
      }

      if (submitButton) {
        submitButton.disabled = true;
      }
      statusEl.textContent = route === 'media' ? copy.creatingLink : transcriptStatusText('creatingLink', copy.creatingLink);
      resultEl.innerHTML = '';

      try {
        if (route === 'media') {
          const presign = await createUploadIntentWithRetry({
            filename: file.name,
            contentType: file.type || 'application/octet-stream',
            customerId: form.customerId.value || undefined,
            projectId: form.projectId.value || undefined,
            meetingDate: form.meetingDate?.value || getTodayDateString(),
            domain: form.domain?.value || undefined,
            engine: selectedEngine,
          });

          statusEl.textContent = copy.uploading;
          await uploadFileToPresignedUrl(presign.uploadUrl, file);

          statusEl.textContent = copy.uploadDone;
          resultEl.innerHTML = renderSuccess(copy, presign);
          bindResultActions(resultEl, copy);
          return;
        }

        let uploadFile = file;
        let contentType = file.type || 'application/octet-stream';
        if (isPdfFile(file)) {
          statusEl.textContent = transcriptStatusText('readingPdf', 'PDF wird gelesen...');
          const pdfText = await extractPdfText(file);
          if (!pdfText.trim()) {
            throw new Error(transcriptCopy.status?.pdfEmpty ?? 'PDF enthaelt keinen Text.');
          }
          uploadFile = new Blob([JSON.stringify({ transcript: pdfText })], { type: 'application/json' });
          contentType = 'application/json';
        } else if (isDocxFile(file)) {
          statusEl.textContent = transcriptStatusText('readingDocx', 'Word-Datei wird gelesen...');
          const docxText = await extractDocxText(file);
          if (!docxText.trim()) {
            throw new Error(transcriptCopy.status?.docxEmpty ?? 'Word-Datei enthaelt keinen Text.');
          }
          uploadFile = new Blob([JSON.stringify({ transcript: docxText })], { type: 'application/json' });
          contentType = 'application/json';
        } else if (isDocFile(file)) {
          throw new Error(transcriptCopy.status?.docUnsupported ?? 'Legacy .doc wird nicht unterstuetzt. Bitte als .docx speichern.');
        }

        const presign = await createUploadIntentWithRetry({
          filename: file.name,
          contentType,
          customerId: form.customerId.value || undefined,
          projectId: form.projectId.value || undefined,
          meetingDate: form.meetingDate?.value || getTodayDateString(),
          domain: form.domain?.value || undefined,
          uploadType: 'transcript',
        });

        statusEl.textContent = transcriptStatusText('uploading', copy.uploading);
        await uploadFileToPresignedUrl(presign.uploadUrl, uploadFile);

        statusEl.textContent = transcriptCopy.status?.finalizing ?? copy.creatingLink;
        await finalizeManualTranscript(presign.conversationId);

        statusEl.textContent = transcriptStatusText('uploadDone', copy.uploadDone);
        resultEl.innerHTML = renderSuccess(transcriptSuccessCopy, { conversationId: presign.conversationId });
        bindResultActions(resultEl, transcriptSuccessCopy);
      } catch (error) {
        console.error(error);
        handleBillingRetryToken(error);
        statusEl.textContent = mapUploadErrorMessage(error, transcriptStatusText('uploadError', copy.uploadError));
      } finally {
        if (submitButton) {
          submitButton.disabled = false;
        }
      }
    });

    return;
  }

  container.innerHTML = html`
    <div class="stack" style="gap:24px;">
      <div class="${uploadCardClass}">
        <h2>${uploadTitle}</h2>
        ${showDescription && uploadDescription ? `<p class="muted">${uploadDescription}</p>` : ''}
        <form class="stack" id="upload-form">
          <label>
            ${uploadFileLabel}
            <input type="file" name="file" accept="audio/*,video/*,.qta" required />
          </label>
          ${isNewRequirements ? '' : engineSelectorMarkup}
          <div class="row">
            <label style="flex:1">
              ${customerLabel}
              <input type="text" name="customerId" placeholder="${copy.customerPlaceholder}" />
            </label>
            <label style="flex:1">
              ${projectLabel}
              <input type="text" name="projectId" placeholder="${copy.projectPlaceholder}" />
            </label>
            <label style="flex:1">
              ${meetingLabel ?? 'Meeting'}
              <input type="date" name="meetingDate" />
            </label>
            <label style="flex:1">
              ${copy.domainLabel ?? 'Themenbereich'}
              <select name="domain">
                ${DOMAIN_OPTIONS.map(
                  (option) => `<option value="${option.value}"${option.value === DEFAULT_DOMAIN ? ' selected' : ''}>${option.label}</option>`,
                ).join('')}
              </select>
            </label>
          </div>
          ${isNewRequirements ? engineSelectorMarkup : ''}
          <div class="form-actions">
            <button type="submit">${copy.button}</button>
            <p class="muted" id="upload-status"></p>
          </div>
        </form>
        ${showPlanNote && user?.plan?.name
          ? `<p class="muted">${copy.planNote} <strong>${user.plan.name} (${user.plan.priceLabel})</strong>. <a href="#/profile">${copy.planNoteCta}</a></p>`
          : ''}
        <div id="upload-result"></div>
      </div>

      <div class="${transcriptCardClass}">
        <h2>${transcriptTitle}</h2>
        ${showTranscriptDescription && transcriptDescription ? `<p class="muted">${transcriptDescription}</p>` : ''}
        <form class="stack" id="transcript-form">
          <label>
            ${transcriptFileLabel}
            <input
              type="file"
              name="transcriptFile"
              accept="${transcriptUploadAccept}"
              required
            />
          </label>
          ${transcriptFormats ? `<p class="muted tiny">${transcriptFormats}</p>` : ''}
          <div class="row">
            <label style="flex:1">
              ${transcriptCustomerLabel}
              <input type="text" name="transcriptCustomerId" placeholder="${copy.customerPlaceholder}" />
            </label>
            <label style="flex:1">
              ${transcriptProjectLabel}
              <input type="text" name="transcriptProjectId" placeholder="${copy.projectPlaceholder}" />
            </label>
            <label style="flex:1">
              ${transcriptMeetingLabel ?? 'Meeting'}
              <input type="date" name="transcriptMeetingDate" />
            </label>
            <label style="flex:1">
              ${transcriptCopy.domainLabel ?? copy.domainLabel ?? 'Themenbereich'}
              <select name="transcriptDomain">
                ${DOMAIN_OPTIONS.map(
                  (option) => `<option value="${option.value}"${option.value === DEFAULT_DOMAIN ? ' selected' : ''}>${option.label}</option>`,
                ).join('')}
              </select>
            </label>
          </div>
          <div class="form-actions">
            <button type="submit">${transcriptCopy.button ?? copy.button}</button>
            <p class="muted" id="transcript-status"></p>
          </div>
        </form>
        <div id="transcript-result"></div>
      </div>
    </div>
  `;

  const form = container.querySelector('#upload-form');
  const statusEl = container.querySelector('#upload-status');
  const resultEl = container.querySelector('#upload-result');
  const submitButton = form.querySelector('button[type="submit"]');
  const meetingInput = form.querySelector('input[name="meetingDate"]');
  const engineButtons = Array.from(container.querySelectorAll('#upload-engine-selector [data-engine]'));
  let selectedEngine = DEFAULT_ENGINE;
  const updateEngineSelection = (engine) => {
    if (!engine) return;
    selectedEngine = engine;
    engineButtons.forEach((button) => {
      const isActive = button.dataset.engine === engine;
      button.classList.toggle('active', isActive);
      button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });
  };
  engineButtons.forEach((button) => {
    button.addEventListener('click', () => updateEngineSelection(button.dataset.engine));
  });
  updateEngineSelection(DEFAULT_ENGINE);

  if (meetingInput && !meetingInput.value) {
    meetingInput.value = getTodayDateString();
  }

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    const file = form.file.files[0];
    if (!file) {
      statusEl.textContent = copy.selectFileWarning;
      return;
    }

    if (submitButton) {
      submitButton.disabled = true;
    }
    statusEl.textContent = copy.creatingLink;

    try {
      const presign = await createUploadIntentWithRetry({
        filename: file.name,
        contentType: file.type || 'application/octet-stream',
        customerId: form.customerId.value || undefined,
        projectId: form.projectId.value || undefined,
        meetingDate: form.meetingDate?.value || getTodayDateString(),
        domain: form.domain?.value || undefined,
        engine: selectedEngine,
      });

      statusEl.textContent = copy.uploading;
      await uploadFileToPresignedUrl(presign.uploadUrl, file);

      statusEl.textContent = copy.uploadDone;
      resultEl.innerHTML = renderSuccess(copy, presign);
      bindResultActions(resultEl, copy);
    } catch (error) {
      console.error(error);
      handleBillingRetryToken(error);
      statusEl.textContent = mapUploadErrorMessage(error, copy.uploadError);
    } finally {
      if (submitButton) {
        submitButton.disabled = false;
      }
    }
  });

  const transcriptForm = container.querySelector('#transcript-form');
  const transcriptStatusEl = container.querySelector('#transcript-status');
  const transcriptResultEl = container.querySelector('#transcript-result');
  const transcriptMeetingInput = transcriptForm.querySelector('input[name="transcriptMeetingDate"]');

  if (transcriptMeetingInput && !transcriptMeetingInput.value) {
    transcriptMeetingInput.value = getTodayDateString();
  }

  transcriptForm.addEventListener('submit', async (event) => {
    event.preventDefault();
    const file = transcriptForm.transcriptFile.files[0];
    if (!file) {
      transcriptStatusEl.textContent = transcriptCopy.selectFileWarning ?? copy.selectFileWarning;
      return;
    }

    transcriptStatusEl.textContent = transcriptStatusText('creatingLink', copy.creatingLink);

    try {
      let uploadFile = file;
      let contentType = file.type || 'application/octet-stream';
      if (isPdfFile(file)) {
        transcriptStatusEl.textContent = transcriptStatusText('readingPdf', 'PDF wird gelesen…');
        const pdfText = await extractPdfText(file);
        if (!pdfText.trim()) {
          throw new Error(transcriptCopy.status?.pdfEmpty ?? 'PDF enthält keinen Text.');
        }
        uploadFile = new Blob([JSON.stringify({ transcript: pdfText })], { type: 'application/json' });
        contentType = 'application/json';
      } else if (isDocxFile(file)) {
        transcriptStatusEl.textContent = transcriptStatusText('readingDocx', 'Word-Datei wird gelesen…');
        const docxText = await extractDocxText(file);
        if (!docxText.trim()) {
          throw new Error(transcriptCopy.status?.docxEmpty ?? 'Word-Datei enthält keinen Text.');
        }
        uploadFile = new Blob([JSON.stringify({ transcript: docxText })], { type: 'application/json' });
        contentType = 'application/json';
      } else if (isDocFile(file)) {
        throw new Error(transcriptCopy.status?.docUnsupported ?? 'Legacy .doc wird nicht unterstützt. Bitte als .docx speichern.');
      }
      const presign = await createUploadIntentWithRetry({
        filename: file.name,
        contentType,
        customerId: transcriptForm.transcriptCustomerId.value || undefined,
        projectId: transcriptForm.transcriptProjectId.value || undefined,
        meetingDate: transcriptForm.transcriptMeetingDate?.value || getTodayDateString(),
        domain: transcriptForm.transcriptDomain?.value || undefined,
        uploadType: 'transcript',
      });

      transcriptStatusEl.textContent = transcriptStatusText('uploading', copy.uploading);
      await uploadFileToPresignedUrl(presign.uploadUrl, uploadFile);

      transcriptStatusEl.textContent = transcriptCopy.status?.finalizing ?? copy.creatingLink;
      await finalizeManualTranscript(presign.conversationId);

      transcriptStatusEl.textContent = transcriptStatusText('uploadDone', copy.uploadDone);
      transcriptResultEl.innerHTML = renderSuccess(transcriptSuccessCopy, { conversationId: presign.conversationId });
      bindResultActions(transcriptResultEl, transcriptSuccessCopy);
    } catch (error) {
      console.error(error);
      handleBillingRetryToken(error);
      transcriptStatusEl.textContent = mapUploadErrorMessage(error, transcriptStatusText('uploadError', copy.uploadError));
    }
  });
};
